/**
 * Read-aloud through the Web Speech synthesis API where the browser has it (desktop Chrome,
 * Edge, and Quest Browser). Nothing is sent anywhere: synthesis is local to the browser.
 */
export function speechAvailable(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

let current: SpeechSynthesisUtterance | undefined;

/** Voices that sound like the host (an English voice, a female one where offered). */
const HOST_VOICE_HINTS = [
  /google uk english female/i,
  /microsoft (libby|sonia|maisie|hazel|aria|jenny|zira)/i,
  /samantha|serena|karen|moira|tessa/i,
  /female/i,
];

/** The voice the host speaks with, or undefined for the browser's default. */
export function hostVoice(): SpeechSynthesisVoice | undefined {
  if (!speechAvailable()) return undefined;
  const voices = window.speechSynthesis.getVoices().filter((v) => v.lang.startsWith('en'));
  for (const hint of HOST_VOICE_HINTS) {
    const v = voices.find((x) => hint.test(x.name));
    if (v) return v;
  }
  return voices.find((v) => v.lang === 'en-GB') ?? voices[0];
}

/** Text as it should be spoken: no markdown marks, ids and dashes read sensibly. */
export function speakable(text: string): string {
  return text
    .replace(/[*_#`>]/g, '')
    .replace(/\s*\n+\s*/g, '. ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Speaks the text, replacing anything still being read. Resolves when it ends or is cut, or
 * after a generous estimate of its length: some browsers never fire the end event (headless
 * ones, a Quest with no voice installed), and nothing may wait on speech forever.
 */
export function speak(
  text: string,
  options: {
    lang?: string;
    voice?: SpeechSynthesisVoice;
    rate?: number;
    /** Called when the voice is heard (or after a second, if the browser never says). */
    onStart?: () => void;
  } = {},
): Promise<void> {
  if (!speechAvailable()) {
    options.onStart?.();
    return Promise.resolve();
  }
  stopSpeaking();
  const said = speakable(text);
  return new Promise((resolve) => {
    const u = new SpeechSynthesisUtterance(said);
    u.lang = options.voice?.lang ?? options.lang ?? 'en-GB';
    if (options.voice) u.voice = options.voice;
    u.rate = options.rate ?? 1.0;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      if (current === u) current = undefined;
      resolve();
    };
    const timer = setTimeout(finish, 2500 + (said.length / 13) * 1000);
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      options.onStart?.();
    };
    u.onstart = start;
    setTimeout(start, 1000);
    u.onend = finish;
    u.onerror = finish;
    current = u;
    window.speechSynthesis.speak(u);
  });
}

export function stopSpeaking(): void {
  if (!speechAvailable()) return;
  current = undefined;
  window.speechSynthesis.cancel();
}

export function isSpeaking(): boolean {
  return speechAvailable() && window.speechSynthesis.speaking;
}
